import {
  Badge,
  CircularProgress,
  IconButton,
  Menu,
  Modal,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import React, { useEffect, useState } from "react"
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone"
import { useThemeContext } from "contexts/ThemeContext"
import { useTranslation } from "react-i18next"
import ArrowRightIcon from "@mui/icons-material/ArrowRight"
import { styles } from "./styles/NotificationsManu.styles"
import NotificationItem from "./NotificationItem"
import useNotificationApi from "modules/notifications"
import { useAuthContext } from "contexts/AuthContext"
import { useNavigate } from "react-router-dom"
import { routes } from "common/navigationRoutes"
import { ACTIVITY_VIEW_MODES } from "pages/student/activity/common/constants"
import useActivityApi from "modules/activities"
import RewardsItem from "pages/student/rewards/RewardsItem"
import useGroupApi from "modules/groups"
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff"

export default function NotificationsMenu({
  sx = {},
  total = 0,
  notificationLastUpdate = "",
}) {
  const { getNotifications, setNotificationSeen, isFetching } =
    useNotificationApi()
  const { getContentByGrade } = useActivityApi()
  const { setSelectedGroup, getSelectedGroup } = useGroupApi()
  const { t } = useTranslation()
  const { userData, selectedGroupData, setSelectedGroupData } = useAuthContext()
  const { bgColor } = useThemeContext()
  const [anchorEl, setAnchorEl] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [openPreview, setOpenPreview] = useState(-1)
  const [totalNotifications, setTotalNotifications] = useState(total)
  const open = Boolean(anchorEl)
  const navigate = useNavigate()
  const theme = useTheme()

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    setTotalNotifications(total)
  }, [total, notificationLastUpdate])

  useEffect(() => {
    if (open) {
      loadNotifications()
    }
  }, [open])

  const loadNotifications = async () => {
    const languageId = userData?.isB2C
      ? userData?.languageToLearn
      : selectedGroupData?.learningLanguageId
    const response = await getNotifications({ languageId })
    if (response) {
      setNotifications(response)
    }
  }

  const onNotificationOpened = async ({
    notificationId,
    notificationTypeId,
    seen,
    argument1,
    groupId,
    reference,
  }) => {
    console.log("Open notification", notificationTypeId, argument1)
    if (
      !seen &&
      notificationTypeId !== 24 &&
      notificationTypeId !== 25 &&
      notificationTypeId !== 26
    ) {
      setNotificationSeen({ notificationId })
      // update notifications state
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification.notificationId === notificationId
            ? { ...notification, seen: true }
            : notification
        )
      )
      setTotalNotifications((prevTotal) => Math.max(0, prevTotal - 1))
    }
    switch (notificationTypeId) {
      case 1:
      case 2:
        // show notification in system
        break
      case 3:
        navigate("/tickets")
        break
      case 4:
      case 5:
      case 7:
      case 20: {
        if (groupId > 0 && selectedGroupData?.groupId !== groupId) {
          const response = await setSelectedGroup({ groupId })
          if (response.status) {
            const res = await getSelectedGroup()
            setSelectedGroupData(res)
          }
        }
        const response = await getContentByGrade({ gradeId: argument1 })
        if (response) {
          const activityUrl = `${routes.ACTIVITY}?groupId=${groupId}&missionId=${response?.missionId}&missionModuleId=${response?.missionModuleId}&activityId=${response.activityId}&view=${ACTIVITY_VIEW_MODES.STUDENT}`
          navigate(activityUrl, { replace: true })
          window.location.reload()
        }
        break
      }
      case 14: {
        if (groupId > 0 && selectedGroupData?.groupId !== groupId) {
          const response = await setSelectedGroup({ groupId })
          if (response.status) {
            const res = await getSelectedGroup()
            setSelectedGroupData(res)
          }
        }
        navigate(routes.MISSIONS, { params: { missionId: argument1 } })
        break
      }
      case 15:
      case 16:
        if (groupId > 0 && selectedGroupData?.groupId !== groupId) {
          const response = await setSelectedGroup({ groupId })
          if (response.status) {
            const res = await getSelectedGroup()
            setSelectedGroupData(res)
          }
        }
        navigate(`${routes.ANNOUNCEMENTS}?highlight=${reference}`)
        break
      case 23: {
        navigate(routes.GAMES, {
          params: {
            type: "challenges",
            view: "reports",
          },
        })
        break
      }
      case 24:
      case 25:
      case 26:
        setOpenPreview(argument1)
        break
      default:
        console.warn("invalid notification type: " + notificationTypeId)
        break
    }
    setAnchorEl(null)
  }

  return (
    <React.Fragment>
      <Modal open={openPreview > 0} onClose={() => setOpenPreview(-1)}>
        <RewardsItem
          params={{
            userItemId: openPreview,
            unreads: 0,
            onClose: (evt) => {
              evt.stopPropagation()
              evt.preventDefault()
              setOpenPreview(-1)
            },
          }}
        />
      </Modal>
      <IconButton
        size="large"
        edge="start"
        color="inherit"
        id={`notification-general-btn`}
        aria-controls={open ? "notification-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={sx}
      >
        <Badge
          badgeContent={totalNotifications}
          color="danger"
          sx={styles.notificationBadge}
        >
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>
      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            "aria-labelledby": `notification-general-btn`,
          },
          paper: {
            // elevation: 0,
            sx: {
              minWidth: 250,
              maxWidth: 400,
              width: "calc(100vw - 100px)",
              border: `1px solid ${theme.palette.grey.lighter2}`,
              p: ".833em 1em",
              color: "rgba(0,0,0,.87)",
              boxShadow:
                "0 2px 4px 0 rgba(34,36,38,.12),0 2px 10px 0 rgba(34,36,38,.15)",
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .notification-btn": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: bgColor,
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Stack direction={"row"} alignItems={"center"}>
          <Typography
            variant="span"
            fontWeight={"bold"}
            color={theme.palette.common.black}
          >
            {t("notifications")}
          </Typography>
          <ArrowRightIcon
            fontSize="small"
            sx={{ color: theme.palette.common.black }}
          />
          <Typography
            variant="body1"
            fontWeight={"bold"}
            sx={styles.notificationHeaderText}
          >
            {t("general")}
          </Typography>
        </Stack>
        {
          <Stack
            sx={[
              styles.notifications,
              notifications.length === 0 && { justifyContent: "safe center" },
            ]}
          >
            {isFetching && notifications.length === 0 && (
              <CircularProgress
                sx={{
                  color: theme.palette.primary.main,
                  width: 50,
                  height: 50,
                  alignSelf: "center",
                }}
                size={"large"}
              />
            )}
            {!isFetching && notifications.length === 0 && (
              <Stack alignItems={"center"} alignSelf={"center"}>
                <NotificationsOffIcon
                  fontSize="large"
                  sx={{ fontSize: 100, color: "#84818a" }}
                />
                <Typography color={"#84818a"}>
                  {t("there_arent_any_notifications")}
                </Typography>
              </Stack>
            )}
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.notificationId}
                onNotificationOpened={(evt) => {
                  evt.stopPropagation()
                  onNotificationOpened({ ...notification })
                }}
                {...notification}
              />
            ))}
          </Stack>
        }
      </Menu>
    </React.Fragment>
  )
}
